import { computed, inject, Injectable } from '@angular/core';
import { taskDetailTokenizer } from '../store/token';
import { injectDispatch } from '@ngrx/signals/events';
import { TASK_DETAIL_EVENTS } from '../store';
import {
  DocumentTaskCreatedPayload,
  MessagePayload,
  QuestionTaskCreatedPayload,
  TaskDocument,
  TaskStatus,
} from '../types';
import { FullNamePipe } from '@shared/pipes/fullname.pipe';
import { SurveyInstanceSignalState } from '@data-access/shared';

@Injectable({
  providedIn: 'root',
})
export class TasksDetailFacadeService {
  private readonly _taskDetailSignalStore = taskDetailTokenizer();
  private _taskDetailEvents = injectDispatch(TASK_DETAIL_EVENTS);
  private _surveyInstanceSignalState = inject(SurveyInstanceSignalState);
  private _fullName = inject(FullNamePipe);

  // signals for loading task detail
  $taskDetail = this._taskDetailSignalStore.$taskDetail;
  $taskMessages = this._taskDetailSignalStore.$messages;
  $isLoadingTaskDetail = this._taskDetailSignalStore.$isLoadingTaskDetail;
  $filteredTask = this._taskDetailSignalStore.$filteredTask;
  $exportText = this._taskDetailSignalStore.$exportText;
  $lastRefreshedTaskId = this._taskDetailSignalStore.$lastRefreshedTaskId;
  $messageCommunicationEstablished =
    this._taskDetailSignalStore.$messageCommunicationEstablished;

  /*
   * @NOTE: SURVEY INSTANCE is loaded the moment submission is loaded. And task detail need access to survey instance info.
   * */
  $surveyInstance = computed(() =>
    this._surveyInstanceSignalState.$surveyInstance(),
  );

  // Computed signal that groups documents by timestamp and author
  $groupedDocuments = computed(() => {
    const taskDetails = this._taskDetailSignalStore.$taskDetail();
    if (!taskDetails?.documents) {
      return [];
    }

    const groups = new Map<
      string,
      {
        timestamp: string;
        author: string;
        documents: TaskDocument[];
      }
    >();

    taskDetails.documents.forEach((doc) => {
      const timestamp = doc.created_dt;
      const author = doc.created_by_user
        ? this._fullName.transform(doc.created_by_user, 'lastFirst')
        : 'Unknown';

      const key = `${timestamp}-${author}`;

      if (!groups.has(key)) {
        groups.set(key, {
          author,
          documents: [],
          timestamp,
        });
      }

      groups.get(key)!.documents.push(doc);
    });

    // Convert to array and sort by timestamp (newest first)
    return Array.from(groups.values()).sort(
      (a, b) =>
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
    );
  });

  /*
   * Method to load task details for a given workspace ID and task instance ID
   * @param workspaceID - The ID of the workspace
   * @param taskInstanceId - The ID of the task instance
   * @param filteredTask - Boolean indicating if the task is filtered
   */
  loadTaskDetail(
    workspaceID: string,
    taskInstanceId: string,
    filteredTask: boolean = false,
  ) {
    this._taskDetailSignalStore.updateTaskDetailFilteredState(filteredTask);
    this._taskDetailEvents.getTaskDetail({
      taskInstanceId,
      workspaceID,
    });
  }

  /**
   * Method to update the status of a task
   * @param workspaceID - The ID of the workspace
   * @param taskInstanceId - The ID of the task instance
   * @param status - The new status to set for the task
   */
  updateTaskStatus(
    workspaceID: string,
    taskInstanceId: string,
    status: TaskStatus,
  ) {
    this._taskDetailEvents.updateTaskStatus({
      status,
      taskInstanceId,
      workspaceID,
    });
  }

  /**
   * Method to clear the current task detail from the store
   **/
  clearTaskDetail() {
    this._taskDetailEvents.clearTaskDetail();
  }

  /**
   * Method to add an entity to a task
   * @param data - The entity data to add
   * @param workspaceID - The ID of the workspace
   **/
  addEntityToTask(data: unknown, workspaceID: string) {
    this._taskDetailEvents.addEntityToTask({ data, workspaceID });
  }

  /**
   * Method to create a message for a specific task
   * @param workspaceID - The ID of the workspace
   * @param taskInstanceId - The ID of the task instance
   * @param message - The message payload to create
   **/
  createTaskMessage(
    workspaceID: string,
    taskInstanceId: string,
    message: MessagePayload,
  ) {
    this._taskDetailEvents.createTaskMessage({
      message,
      taskInstanceId,
      workspaceID,
    });
  }

  /**
   * Method to fetch or create a document-related task
   * @param workspaceID - The ID of the workspace
   * @param payload - The payload containing document task details
   **/
  getDocumentTask(workspaceID: string, payload: DocumentTaskCreatedPayload) {
    // First, search for existing document tasks
    this._taskDetailEvents.searchDocumentTask({
      payload,
      workspaceID,
    });
  }

  /**
   * Method to fetch or create a question-related task
   * @param workspaceID - The ID of the workspace
   * @param payload - The payload containing question task details
   **/
  getQuestionTask(workspaceID: string, payload: QuestionTaskCreatedPayload) {
    // First, search for existing document tasks
    this._taskDetailEvents.searchQuestionTask({
      payload,
      workspaceID,
    });
  }

  /**
   * Facade method to select a task and optionally fetch its messages.
   * @param workspaceID - The ID of the workspace containing the task.
   * @param taskInstanceId - The ID of the task instance to select.
   * @param threadId - (Optional) The ID of the thread whose messages are to be fetched.
   */
  selectTask(workspaceID: string, taskInstanceId: string, threadId?: string) {
    this._taskDetailEvents.getTaskDetail({
      taskInstanceId,
      workspaceID,
    });
    if (threadId)
      this._taskDetailEvents.getTaskMessages({
        threadId: threadId || '',
        workspaceID,
      });
    else
      this._taskDetailEvents.patchMessages({
        messages: [],
      });
  }

  /*
   * Facade method to export messages from a specific thread in a workspace.
   * @param workspaceID - The ID of the workspace containing the thread.
   * @param threadId - The ID of the thread whose messages are to be exported.
   */
  exportThreadMessages(workspaceID: string, threadId: string) {
    this._taskDetailEvents.exportThreadMessages({
      threadId,
      workspaceID,
    });
  }

  /**
   * Facade method to fetch messages from a specific thread in a workspace.
   * @param workspaceID - The ID of the workspace containing the thread.
   * @param threadId - The ID of the thread whose messages are to be fetched.
   */
  getThreadMessages(workspaceID: string, threadId: string) {
    this._taskDetailEvents.getTaskMessages({
      threadId,
      workspaceID,
    });
  }
}
